import React from 'react';
import { useSelector } from 'react-redux';
import { ScrollMapsData } from '../types';
import { Box, Column, Container, Divider, Pane, Toggle, Typography } from '@fullstory/components';
import { useFlags, useNotifications } from '@fullstory/react-hooks';
import { EngagementMapsStatus } from '../../Components/EngagementMapsStatus';
import { EngagementMapsState, SegmentData } from '../../Components/types';
import { EngagementMapsDetailsCard } from '../../Components/EngagementMapsDetailsCard';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { useHistory } from 'react-router-dom';
import { generateRoutePath, SETTINGS_PAGES } from '@fullstory/routes';
import { fullstorySelectors } from '@fullstory/redux';
import { SegmentRow } from '../../Components/SegmentRow';
import { PageDefinitionRow } from '../../Components/PageDefinitionRow';
import { PagePathRow } from '../../Components/PagePathRow';
import { TimeFrameRow } from '../../Components/TimeFrameRow';
import { DeviceRow } from '../../Components/DeviceRow';
import { PageViewsRow } from '../../Components/PageViewsRow';
import { FormattedMessage, useIntl } from 'react-intl';
import { ContactUs } from '../../Components/ContactUs';

const { getOrgId } = fullstorySelectors;

const DefaultTextVariant = 'body3';
const DefaultTextColor = 'neutrals-n8';

export interface ScrollMapsSidebarProps {
  /**
   * The current state of loading the Scroll Maps Data. This is used to determine what message to display.
   */
  dataState: EngagementMapsState;
  /**
   * A callback provided by ScrollMapsSideBarContainer to update the state if the user wants to bypass the
   * internal scroll warning.
   */
  onInternalScrollWarningButtonClick: () => void;
  /**
   * The state of the toggle for whether Page Fold data is shown.
   */
  medianPageFoldToggleChecked: boolean;
  /**
   * A callback provided when the user wishes to toggle whether Page Fold data is shown.
   */
  onMedianPageFoldToggle: (checked: boolean) => void;
  /**
   * Information about the segment being used to create the results
   */
  segmentData: SegmentData | null;
  /**
   * A callback for handling navigating back to the segment definition upon click
   */
  navigateToSegment: () => void;
  /**
   * Information to display about the Scroll Maps itself upon successful loading.
   */
  scrollMapsData: ScrollMapsData | null;
}

/**
 * The Scroll Maps sidebar responsible for displaying information about the loading state of Scroll Maps.
 */
export const ScrollMapsSidebar: React.FC<ScrollMapsSidebarProps> = ({
  dataState,
  onInternalScrollWarningButtonClick,
  medianPageFoldToggleChecked,
  onMedianPageFoldToggle,
  segmentData,
  navigateToSegment,
  scrollMapsData,
}) => {
  const history = useHistory();
  const notifications = useNotifications();
  const flags = useFlags();
  const canAccess = flags.hasBit(Flag.PermModifyPageDefinitions);
  const orgId = useSelector(getOrgId);
  const warningMessage = (
    <FormattedMessage
      defaultMessage="It looks like this page includes components or content areas that scroll independently of the rest of the page. Fullstory Scroll Maps work best with pages that scroll in their entirety, which is known as document-level scrolling. The scroll map data displayed may be misleading."
      id="ZfFnVN"
    />
  );

  const dataMessage = (
    <FormattedMessage
      defaultMessage="Click and drag the page marker to the left or use your mouse to scroll. Changing the marker's position will display the percentage of page views in which a user scrolled to at least that depth."
      id="CpgDvG"
    />
  );

  const intl = useIntl();
  let data = dataMessage;
  if (scrollMapsData && segmentData) {
    if (
      notifications &&
      scrollMapsData.documentHeight &&
      scrollMapsData.documentHeight > 0 &&
      scrollMapsData.medianPageFold > scrollMapsData.documentHeight
    ) {
      notifications.info(
        intl.formatMessage({
          defaultMessage: 'The page height rendered is shorter than the median viewport height.',
          id: 'ggZUZB',
        }),
        intl.formatMessage({
          defaultMessage: 'Median Viewport Height Offscreen Warning',
          id: 'XM+j+6',
        }),
        true,
      );
    }

    data = (
      <>
        <Pane border="standard" backgroundColor="default" borderRadius={6} overflow="hidden">
          <EngagementMapsDetailsCard
            name={intl.formatMessage({ defaultMessage: 'Scroll Map', id: '5uyi+f' })}
          >
            <SegmentRow segmentName={segmentData.segment ?? null} onRowClick={navigateToSegment} />
            {segmentData.pageDefinition ? (
              <PageDefinitionRow
                pageDefinition={segmentData.pageDefinition}
                onPageEdit={() => history.push(generateRoutePath(SETTINGS_PAGES, { orgId }))}
                canAccess={canAccess}
              />
            ) : (
              <PagePathRow
                canonicalPagePath={segmentData.canonicalPagePath}
                domain={segmentData.pageDomain}
              />
            )}
            <TimeFrameRow
              timeFrame={segmentData.timeFrame}
              showTimeFrameWarning={segmentData.showTimeFrameWarning}
            />
            <DeviceRow device={segmentData.device} />
            <PageViewsRow pageViews={scrollMapsData.pageViews} />
            <Box paddingBottom={16}>
              <Divider variant="medium" />
            </Box>
            <Box paddingBottom={4}>
              <Container verticalAlign="center">
                <Column>
                  <Typography variant="heading5" color="neutrals-n11">
                    <FormattedMessage defaultMessage="Show Median Fold" id="pLrDGQ" />
                  </Typography>
                </Column>
                <Container align="end">
                  {scrollMapsData.medianPageFold > 0 && (
                    <Box
                      data-fs="scroll-maps-sidebar-page-fold-toggle"
                      data-active={medianPageFoldToggleChecked}
                    >
                      <Toggle
                        checked={medianPageFoldToggleChecked}
                        onToggle={onMedianPageFoldToggle}
                      />
                    </Box>
                  )}
                </Container>
              </Container>
            </Box>
            <Box paddingBottom={24}>
              <Typography variant={DefaultTextVariant} color={DefaultTextColor}>
                {scrollMapsData.medianPageFold
                  ? intl.formatMessage({
                      defaultMessage:
                        'Display the median viewport height for users when they first load the page.',
                      id: 'ckX0Id',
                    })
                  : intl.formatMessage({
                      defaultMessage: 'No fold data was found for this page.',
                      id: 'wh6Q1X',
                    })}
              </Typography>
            </Box>
          </EngagementMapsDetailsCard>
        </Pane>
        <Pane padding={16}>
          <Typography variant={DefaultTextVariant} color={DefaultTextColor} textAlign="center">
            {dataMessage}
          </Typography>
        </Pane>
        <ContactUs />
      </>
    );
  }

  const mapStatus = (
    <EngagementMapsStatus
      name={intl.formatMessage({ defaultMessage: 'Scroll Maps', id: 'NJ79p2' })}
      state={dataState}
      data={data}
      warningMessage={warningMessage}
      onWarningButtonClick={onInternalScrollWarningButtonClick}
    />
  );

  if (dataState === 'data' && scrollMapsData) {
    return (
      <Box data-component="ScrollMapsSidebar" data-fs="scroll-maps-sidebar">
        {mapStatus}
      </Box>
    );
  } else {
    return (
      <Pane
        data-component="ScrollMapsSidebar"
        data-fs="scroll-maps-sidebar"
        backgroundColor="secondary"
        borderRadius={6}
      >
        {mapStatus}
      </Pane>
    );
  }
};
